import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserConsumer } from "../../hooks/context/UserContext";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import { formatDateTimeUTC } from "../../utils/formatDateTime";

type SectionBlock =
  | { kind: "paragraphs"; titleKey: string; bodyKey: string }
  | { kind: "list"; titleKey: string; introKey: string; itemKeys: string[] }
  | { kind: "plain"; titleKey: string; bodyKey: string };

const SECTIONS: SectionBlock[] = [
  { kind: "paragraphs", titleKey: "privacy_s1_title", bodyKey: "privacy_s1_body" },
  {
    kind: "list",
    titleKey: "privacy_s2_title",
    introKey: "privacy_s2_body",
    itemKeys: [
      "privacy_s2_li_1",
      "privacy_s2_li_2",
      "privacy_s2_li_3",
    ],
  },
  { kind: "plain", titleKey: "privacy_s3_title", bodyKey: "privacy_s3_body" },
  { kind: "plain", titleKey: "privacy_s4_title", bodyKey: "privacy_s4_body" },
  { kind: "plain", titleKey: "privacy_s5_title", bodyKey: "privacy_s5_body" },
  { kind: "plain", titleKey: "privacy_s6_title", bodyKey: "privacy_s6_body" },
  { kind: "plain", titleKey: "privacy_s7_title", bodyKey: "privacy_s7_body" },
  { kind: "plain", titleKey: "privacy_s8_title", bodyKey: "privacy_s8_body" },
  { kind: "plain", titleKey: "privacy_s9_title", bodyKey: "privacy_s9_body" },
  { kind: "paragraphs", titleKey: "privacy_s10_title", bodyKey: "privacy_s10_body" },
];

const PrivacyPolicy = () => {
  const { t, i18n } = useTranslation();
  const { theme } = UserConsumer();
  const isClient = theme === "client";
  const accentIconWrap = isClient ? "bg-customerColor" : "bg-paretnerColor";
  const accentStripe = isClient ? "bg-customerColor" : "bg-paretnerColor";
  const heroGradient = isClient
    ? "from-[#34a83518] via-white to-white"
    : "from-[#fe830018] via-white to-white";
  const ctaClass = isClient
    ? "bg-customerColor hover:bg-[#2d942f] text-white"
    : "bg-paretnerColor hover:bg-[#e67600] text-white";

  return (
    <Box
      component="article"
      className="mt-12 sm:mt-16 mb-16 sm:mb-24"
      dir={i18n.dir()}
      sx={{ maxWidth: "860px", mx: "auto", px: { xs: 1, sm: 2 } }}
    >
      <header
        className={`relative overflow-hidden rounded-3xl border border-gray-100 bg-gradient-to-br ${heroGradient} px-6 py-10 sm:px-10 sm:py-14 shadow-sm`}
      >
        <div
          className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full opacity-40 blur-3xl sm:h-64 sm:w-64"
          style={{ background: isClient ? "#34A835" : "#FE8300" }}
        />
        <div className="relative flex flex-col items-center text-center">
          <div
            className={`mb-5 flex h-16 w-16 items-center justify-center rounded-2xl shadow-lg ${accentIconWrap} text-white`}
          >
            <GppGoodOutlinedIcon sx={{ fontSize: 36 }} />
          </div>
          <h1 className="text-darkColor font-extrabold text-3xl sm:text-4xl tracking-tight mb-3">
            {t("privacy_policy")}
          </h1>
          <p className="text-gray-600 text-base sm:text-lg max-w-xl leading-relaxed mb-2">
            {t("privacy_hero_tagline")}
          </p>
          <p className="text-sm text-gray-500 font-medium">
            {t("privacy_last_updated")}
          </p>
        </div>
      </header>

      <div className="mt-10 sm:mt-12 flex flex-col gap-6 sm:gap-8">
        {SECTIONS.map((section, index) => (
          <section
            key={section.titleKey}
            className="rounded-2xl border border-gray-100 bg-white p-6 sm:p-8 shadow-[0_4px_24px_rgba(55,55,55,0.06)] transition-shadow hover:shadow-[0_8px_32px_rgba(55,55,55,0.08)]"
          >
            <div className="flex items-start gap-3 sm:gap-4">
              <span
                className={`mt-1.5 h-[1.35rem] w-1 shrink-0 rounded-full ${accentStripe}`}
                aria-hidden
              />
              <h2 className="text-darkColor font-bold text-lg sm:text-xl leading-snug">
                {index + 1}. {t(section.titleKey)}
              </h2>
            </div>

            <div className="mt-5 text-gray-600 leading-[1.75] text-[15px] sm:text-base space-y-4">
              {section.kind === "list" && (
                <>
                  <p>{t(section.introKey)}</p>
                  <ul className="list-none space-y-3 ps-0 m-0">
                    {section.itemKeys.map((key) => (
                      <li key={key} className="flex gap-3">
                        <span
                          className={`mt-2 h-1.5 w-1.5 shrink-0 rounded-full ${isClient ? "bg-customerColor" : "bg-paretnerColor"}`}
                          aria-hidden
                        />
                        <span>{t(key)}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {(section.kind === "paragraphs" || section.kind === "plain") && (
                <>
                  {t(section.bodyKey)
                    .split("\n\n")
                    .filter(Boolean)
                    .map((para, i) => (
                      <p key={i} className="whitespace-pre-line">
                        {para}
                      </p>
                    ))}
                </>
              )}
            </div>
          </section>
        ))}
      </div>

      <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 rounded-2xl border border-dashed border-gray-200 bg-gray-50/80 px-6 py-8">
        <p className="text-center text-gray-600 text-sm sm:text-base max-w-md">
          {t("contact_info2")}
        </p>
        <Button
          component={Link}
          to="/contact"
          variant="contained"
          disableElevation
          className={`${ctaClass} !px-6 !py-2 !rounded-xl !normal-case !font-semibold`}
        >
          {t("privacy_cta_contact")}
        </Button>
      </div>
    </Box>
  );
};

export default PrivacyPolicy;
